import React, { useEffect, useRef } from 'react'
import { Animated, Easing, View, ViewStyle } from 'react-native'

import CloseIcon from '@common/assets/svg/CloseIcon'
import ErrorIcon from '@common/assets/svg/ErrorIcon'
import InfoIcon from '@common/assets/svg/InfoIcon'
import SuccessIcon from '@common/assets/svg/SuccessIcon'
import WarningIcon from '@common/assets/svg/WarningIcon'
import Text from '@common/components/Text'
import { isWeb } from '@common/config/env'
import { useTranslation } from '@common/config/localization'
import { splitAnchor, ToastAnchor } from '@common/contexts/toastContext/config'
import {
  ENTER_DURATION_MS,
  EXIT_DURATION_MS,
  TOAST_CARD_MAX_WIDTH,
  TOAST_CARD_WIDTH
} from '@common/contexts/toastContext/constants'
import { VisibleToast } from '@common/contexts/toastContext/types'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import common, { BORDER_RADIUS_SECONDARY } from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'
import { AnimatedPressable, useMultiHover } from '@web/hooks/useHover'
import { getUiType } from '@web/utils/uiType'

import ToastActionLink from './ToastActionLink'
import ToastText from './ToastText'

const { isPopup } = getUiType()

const ICON_MAP = {
  error: ErrorIcon,
  warning: WarningIcon,
  success: SuccessIcon,
  info: InfoIcon,
  info2: InfoIcon
}

const ANIMATION_VALUES: {
  property: keyof ViewStyle
  from: number
  to: number
}[] = [
  {
    property: 'width',
    from: 20,
    to: 32
  },
  {
    property: 'height',
    from: 20,
    to: 32
  },
  {
    property: 'top',
    from: 0.5,
    to: -5.5
  },
  {
    property: 'right',
    from: 0.5,
    to: -5.5
  }
]

const ENTER_OFFSET = 16

// useMultiHover drops layout properties (width/height/top/right) on native —
// they can't share a node with native-driven values — so on iOS/Android the
// close button used to get no size at all: an absolutely positioned Pressable
// shrink-wrapped around the 8×8 icon, which is practically untappable (#550).
// Native gets the resting size of the web animation statically instead.
const NATIVE_CLOSE_STYLE: ViewStyle = { width: 20, height: 20, top: 0.5, right: 0.5 }

// Grows the touch target to ~44pt (Apple HIG) without changing the visuals.
const CLOSE_HIT_SLOP = { top: 12, bottom: 12, left: 12, right: 12 }

const Toast = ({
  text,
  title,
  description,
  action,
  type = 'success',
  id,
  phase,
  anchor,
  removeToast,
  isTypeLabelHidden
}: VisibleToast & {
  anchor: ToastAnchor
  removeToast: (id: number) => void
}) => {
  const { theme } = useTheme()
  const { t } = useTranslation()

  const Icon = ICON_MAP[type]

  const [bindAnim, animStyle] = useMultiHover({
    values: ANIMATION_VALUES
  })

  const [edge, align] = splitAnchor(anchor)
  const isCentered = align === 'center'

  // Slides in from the edge it is anchored to.
  const enterAnim = useRef(new Animated.Value(0)).current

  useEffect(() => {
    Animated.timing(enterAnim, {
      toValue: phase === 'in' ? 1 : 0,
      duration: phase === 'in' ? ENTER_DURATION_MS : EXIT_DURATION_MS,
      easing: phase === 'in' ? Easing.out(Easing.cubic) : Easing.in(Easing.cubic),
      useNativeDriver: !isWeb
    }).start()
  }, [phase, enterAnim])

  const translateY = enterAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [edge === 'bottom' ? ENTER_OFFSET : -ENTER_OFFSET, 0]
  })

  // No title given: render exactly the single line the old toast did, so the
  // 250+ existing addToast(text) call sites look and read the same.
  const heading = title ?? text

  return (
    <Animated.View
      style={{
        ...(isCentered
          ? { width: '100%', maxWidth: TOAST_CARD_MAX_WIDTH }
          : { width: TOAST_CARD_WIDTH, maxWidth: '100%' }),
        borderRadius: BORDER_RADIUS_SECONDARY,
        ...spacings.mbTy,
        ...common.shadowSecondary,
        pointerEvents: 'auto',
        opacity: enterAnim,
        transform: [{ translateY }]
      }}
      testID={`${type}-${id}`}
    >
      <View
        style={[
          !isPopup ? spacings.ph : spacings.phSm,
          !isPopup ? spacings.pv : spacings.pvSm,
          flexbox.directionRow,
          {
            borderRadius: BORDER_RADIUS_SECONDARY,
            borderWidth: 1,
            backgroundColor: theme[`${type}Background`],
            borderColor: theme[`${type}Decorative`]
          }
        ]}
      >
        <Icon width={20} height={20} color={theme[`${type}Decorative`]} />

        <View style={[flexbox.flex1, spacings.mlTy]}>
          <View style={spacings.mrXl}>
            <ToastText
              testID={`toast-title-${id}`}
              text={heading}
              prefix={
                !isTypeLabelHidden ? (
                  <Text
                    selectable
                    appearance={`${type}Text`}
                    fontSize={14}
                    weight="semiBold"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {type}:{' '}
                  </Text>
                ) : null
              }
              appearance={`${type}Text`}
              linkAppearance={`${type}Text`}
              fontSize={14}
              weight="semiBold"
            />

            {!!description && (
              <ToastText
                testID={`toast-description-${id}`}
                text={description}
                appearance="secondaryText"
                linkAppearance="linkText"
                fontSize={12}
                weight="regular"
                style={spacings.mtMi}
              />
            )}

            {!!action && (
              <ToastActionLink
                testID={`toast-action-${id}`}
                text={action.text}
                onPress={action.onPress}
              />
            )}
          </View>

          <AnimatedPressable
            {...bindAnim}
            testID={`toast-close-${id}`}
            accessibilityRole="button"
            accessibilityLabel={t('Close')}
            hitSlop={CLOSE_HIT_SLOP}
            style={{
              ...flexbox.center,
              position: 'absolute',
              right: 0,
              backgroundColor: theme.quaternaryBackground,
              borderRadius: 16,
              ...(isWeb ? {} : NATIVE_CLOSE_STYLE),
              ...animStyle
            }}
            onPress={() => removeToast(id)}
          >
            <CloseIcon width={8} height={8} color={theme[`${type}Decorative`]} strokeWidth="2.5" />
          </AnimatedPressable>
        </View>
      </View>
    </Animated.View>
  )
}

export default Toast
